import React, { useEffect, useState } from "react";

import { Card, useMediaQuery } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  Datagrid,
  EditButton,
  FilterButton,
  FunctionField,
  ListContextProvider,
  ListToolbar,
  RefreshButton,
  ShowButton,
  SimpleList,
  TextField,
  Title,
  TopToolbar,
  useListContext,
} from "react-admin";

import { useSearchableList } from "../../../dataProvider/useSearchableList";
import { UserStatusIcon } from "../../../managerApp/resources/users/UserStatusIcon";
import { ChevronRightIcon } from "../../../components/common/iconography";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { SearchField } from "../../../components/common/SearchField";
import { Pagination } from "../../../components/Pagination";
import { useUser } from "../../../authenticatorApp/UserContext";

const useStyles = makeStyles((theme) => ({
  accountField: {
    maxWidth: "16rem",
    minWidth: "16rem",
  },
  statusIcon: {
    marginTop: theme.spacing(1),
  },
}));

const renderName = (record) => {
  return `${record.lastName}, ${record.firstName}`;
};

const CustomerSearchField = ({ label, alwaysOn, ...props }) => {
  const listContext = useListContext(props);
  const { displayedFilters, filterValues, setFilters } = listContext;

  const handleSearch = (value) => {
    debugger;
    if (value) {
      const nextFilterValues = { ...filterValues };
      delete nextFilterValues.or;

      const filters = {
        ...nextFilterValues,
        or: [
          { lastName: { matchPhrasePrefix: `${value}` } },
          { lastName: { wildcard: `*${value}*` } },
          { email: { matchPhrasePrefix: `${value}` } },
          { email: { wildcard: `*${value}*` } },
        ],
      };

      setFilters(filters, displayedFilters);
    } else {
      const nextFilterValues = { ...filterValues };
      delete nextFilterValues.or;
      setFilters(nextFilterValues, displayedFilters);
    }
  };

  return (
    <SearchField
      id="searchTerm"
      label={label}
      margin="dense"
      variant="outlined"
      alwaysOn={alwaysOn}
      handleSearch={handleSearch}
    />
  );
};

const getListFilters = () => [<CustomerSearchField label="Search" alwaysOn />];

const ListActions = ({ basePath, isXSmall, ...props }) => {
  return !isXSmall ? (
    <TopToolbar>
      <FilterButton />
      <RefreshButton />
    </TopToolbar>
  ) : null;
};

export const CustomerList = (props) => {
  const { appUser } = useUser();
  const classes = useStyles();
  const isXSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const lteMedium = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const [defaultFilter] = useState({
    accountId: appUser?.accountId,
    activeLastName: { beginsWith: { active: "true" } },
  });

  const defaultSearchFilter = {
    accountId: { eq: appUser?.accountId },
    active: { eq: "true" },
  };

  // Include key & search to display default sort order.
  const defaultSort = {
    field: `{"key":"customerByAccountByActiveByName", "search":"lastName"}`,
    order: "ASC",
  };

  // memorize perPage settings
  // see useEffect [listContext?.perPage]) below
  const sessionPerPage = localStorage.getItem(`${props.resource}_perPage`);
  const [defaultPerPage, setDefaultPerPage] = useState(
    sessionPerPage ? Number(sessionPerPage) : 10
  );

  const listContext = useSearchableList({
    ...props,
    defaultTitle: "Users",
    defaultFilter: defaultFilter,
    defaultSort: defaultSort,
    defaultSearchFilter: defaultSearchFilter,
    defaultPerPage: defaultPerPage,
    resource: "customerReloadYardAccounts",
  });

  useEffect(() => {
    if (listContext?.perPage) {
      localStorage.setItem(`${props.resource}_perPage`, listContext.perPage);
      setDefaultPerPage(Number(listContext.perPage));
    }
  }, [listContext?.perPage]);

  useEffect(() => {
    if (listContext?.filter) {
      // reset list on automatic stale list reset
      // Extract the key query from defaultSort
      const fieldObj = JSON.parse(defaultSort.field);
      const keyToCheck = fieldObj.key;

      // Check for the key's presence in filter
      const hasKey = keyToCheck in listContext.filter;
      const hasFilterValues = Object.keys(listContext.filterValues).length > 0;
      if (hasKey && hasFilterValues) listContext.setFilters({});
    }
  }, [listContext?.filter]);

  return (
    <ListContextProvider value={listContext}>
      <Title title={<ForceTitle title="Users" />} />
      <ListToolbar
        {...props}
        filters={getListFilters()}
        actions={<ListActions isXSmall={isXSmall} />}
      />
      <Card>
        <CustomerGrid
          appUser={appUser}
          isXSmall={isXSmall}
          lteMedium={lteMedium}
          classes={classes}
        />
        <Pagination {...listContext} />
      </Card>
    </ListContextProvider>
  );
};

const canEditProfile = (record, appUser) => {
  return record.id === appUser.id;
};

const rowClickHandler = (record, appUser) => {
  return canEditProfile(record, appUser) ? "edit" : "show";
};

const CustomerGrid = (props) => {
  const { appUser, isXSmall, lteMedium, classes } = props;
  const { data, ids, page, ...context } = useListContext();

  // Transform the data here
  const transformedData = {};
  const transformedIds = [];

  if (data && ids) {
    ids.forEach((id) => {
      const record = data[id];
      if (record && record.customer) {
        // Transform the record here, for example, use the user subrecord
        transformedData[record.customerId] = {
          ...record.customer,
          accountName: record.accountName,
        };
        transformedIds.push(record.customerId);
      }
    });
  }

  const newContext = {
    ...context,
    data: transformedData,
    ids: transformedIds,
    basePath: "/customers",
    resource: "customers",
  };

  return (
    <ListContextProvider value={newContext}>
      {isXSmall ? (
        <SimpleList
          primaryText={renderName}
          secondaryText={(record) => record.email}
          tertiaryText={(record) => record.accountName}
          linkType={(record) => rowClickHandler(record, appUser)}
          leftIcon={(record) => <UserStatusIcon record={record} />}
          rightIcon={() => (
            <ChevronRightIcon
              fontSize="large"
              color="secondary"
              style={{ marginLeft: 40, marginTop: 10 }}
            />
          )}
        />
      ) : lteMedium ? (
        <Datagrid
          rowClick={(jsonRecord, basePath, record) =>
            rowClickHandler(record, appUser)
          }
        >
          <FunctionField
            source="lastName"
            label="Name"
            render={renderName}
            sortable={true}
            sortBy={`{"key":"customerByAccountByActiveByName", "search":"lastName"}`}
          />
          <UserStatusIcon
            source="status"
            label="Status"
            classes={classes.statusIcon}
            sortable={false}
          />
          <TextField source="email" label="Email" sortable={false} />
        </Datagrid>
      ) : (
        <Datagrid
          rowClick={(jsonRecord, basePath, record) =>
            rowClickHandler(record, appUser)
          }
        >
          <FunctionField
            source="lastName"
            label="Name"
            render={renderName}
            sortable={true}
            sortBy={`{"key":"customerByAccountByActiveByName", "search":"lastName"}`}
          />
          <UserStatusIcon
            source="status"
            label="Status"
            classes={classes.statusIcon}
            sortable={false}
          />
          <TextField source="email" label="Email" sortable={false} />
          <TextField source="phone" label="Phone" sortable={false} />
          <FunctionField
            render={(record) =>
              canEditProfile(record, appUser) ? (
                <EditButton record={record} />
              ) : (
                <ShowButton record={record} />
              )
            }
          />
        </Datagrid>
      )}
    </ListContextProvider>
  );
};
